import { H1, Icon, Overlay, Tag, Button } from "@blueprintjs/core"
import React from "react"
import classNames from "classnames"

import cssUtils from "./utilsCss.module.css"
import css from "./NotificationsPermissionsRequest.module.css"
import { useTranslation } from "react-i18next"

interface MicrophonePermissionOverlayProps {
  isOpen: boolean
  error: "permission-denied" | "not-supported" | "unknown" | null
  onClose: () => void
  onRetry: () => void
}

const MicrophonePermissionOverlay: React.FC<
  MicrophonePermissionOverlayProps
> = ({ isOpen, error, onClose, onRetry }) => {
  const { t } = useTranslation()
  if (!isOpen) return null

  const getErrorContent = () => {
    switch (error) {
      case "permission-denied":
        return {
          title: t("app.microphone.deniedTitle"),
          message: t("app.microphone.deniedText"),
          icon: "microphone" as const,
          showRetry: true,
        }
      case "not-supported":
        return {
          title: t("app.microphone.notAvailableTitle"),
          message: t("app.microphone.notAvailableText"),
          icon: "error" as const,
          showRetry: false,
        }
      case "unknown":
      default:
        return {
          title: t("app.microphone.errorTitle"),
          message: t("app.microphone.errorText"),
          icon: "warning-sign" as const,
          showRetry: true,
        }
    }
  }

  const errorContent = getErrorContent()

  return (
    <Overlay
      isOpen={isOpen}
      enforceFocus={true}
      onClose={onClose}
      canOutsideClickClose={true}
      className={css.overlay}
    >
      <div className={classNames(css.container, cssUtils.center)}>
        <div>
          <Icon icon={errorContent.icon} size={48} />
        </div>
        <div className={css.info}>
          <H1>{errorContent.title}</H1>
          <p>{errorContent.message}</p>
          <div className={css.actions}>
            {errorContent.showRetry && (
              <Button
                onClick={onRetry}
                intent="primary"
                large
                className={css.actionButton}
              >
                {t("app.actions.tryAgainCapital")}
              </Button>
            )}
            <Tag onClick={onClose} interactive large round>
              {t("app.actions.gotIt")}
            </Tag>
          </div>
        </div>
      </div>
    </Overlay>
  )
}

export default MicrophonePermissionOverlay
